// Import translation files
import enTranslations from "../translations/en.json"
import htTranslations from "../translations/ht.json"

export type Language = "en" | "ht"
export type TranslationKey = string

export interface TranslationContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: TranslationKey, params?: Record<string, string | number>) => string
  availableLanguages: { code: Language; name: string; nativeName: string }[]
}

const translations = {
  en: enTranslations,
  ht: htTranslations,
}

// Available languages with their display names
export const availableLanguages = [
  { code: "ht" as Language, name: "Haitian Creole", nativeName: "Kreyòl Ayisyen" },
  { code: "en" as Language, name: "English", nativeName: "English" },
]

// Default language
export const DEFAULT_LANGUAGE: Language = "ht"

// Storage key for language preference
export const LANGUAGE_STORAGE_KEY = "quiz-app-language"

// Pure translation function
export function createTranslationFunction(language: Language) {
  return (key: TranslationKey, params?: Record<string, string | number>): string => {
    const keys = key.split(".")
    let value: any = translations[language]

    // Navigate through nested keys
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k]
      } else {
        // Fallback to English if key not found in current language
        value = translations.en
        for (const fallbackKey of keys) {
          if (value && typeof value === "object" && fallbackKey in value) {
            value = value[fallbackKey]
          } else {
            console.warn(`Translation key "${key}" not found in ${language} or English fallback`)
            return key // Return the key itself as fallback
          }
        }
        break
      }
    }

    if (typeof value !== "string") {
      console.warn(`Translation key "${key}" does not resolve to a string`)
      return key
    }

    // Handle parameter interpolation with {{param}} syntax
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey]?.toString() || match
      })
    }

    return value
  }
}

// Get saved language preference
export function getSavedLanguage(): Language | null {
  if (typeof window === "undefined") return null

  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language
  return saved && translations[saved] ? saved : null
}

// Save language preference
export function saveLanguage(language: Language): void {
  if (typeof window === "undefined") return

  localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
}

// Detect browser language
export function detectBrowserLanguage(): Language {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE

  const browserLang = navigator.language.split("-")[0] as Language
  return translations[browserLang] ? browserLang : DEFAULT_LANGUAGE
}

// Get initial language (saved > browser > default)
export function getInitialLanguage(): Language {
  const saved = getSavedLanguage()
  if (saved) return saved

  return detectBrowserLanguage()
}

// Validate if a language is supported
export function isValidLanguage(lang: string): lang is Language {
  return lang in translations
}
